/* DoctorWearScheduleReview — doctor's side of the iter95 wear schedule flow.
 *
 * Renders on the Plan tab of CaseDetail (CoE workflow only — self-plan cases
 * don't need this because the doctor IS the planner). Surfaces:
 *  - The planner's authored schedule as a read-only summary.
 *  - "Override schedule" CTA → opens a dialog with side-by-side compare:
 *      left = planner's locked schedule
 *      right = doctor's editable schedule (defaults to planner's values)
 *    Save → POST /api/doctor/cases/{id}/wear-schedule-override.
 *    Reset → DELETE /api/doctor/cases/{id}/wear-schedule-override.
 *
 * If the doctor has already overridden, the summary shows the doctor's effective
 * values + a "Reset to planner's schedule" button.
 */
import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { ClockClockwise, ArrowsCounterClockwise, CheckCircle } from "@phosphor-icons/react";
import WearScheduleSection, { computeTotalWearDays } from "@/components/case/WearScheduleSection";


const fmtDateTime = (iso) => {
    if (!iso) return "";
    try {
        return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
    } catch { return iso.slice(0, 10); }
};


export default function DoctorWearScheduleReview({ c, onChange }) {
    const plan = c?.plan || {};
    const plannerSchedule = useMemo(() => ({
        default_days: plan.wear_schedule_default_days,
        overrides: plan.wear_schedule_overrides || [],
    }), [plan.wear_schedule_default_days, plan.wear_schedule_overrides]);

    const hasDoctorOverride =
        plan.doctor_wear_schedule_default_days != null ||
        (Array.isArray(plan.doctor_wear_schedule_overrides) && plan.doctor_wear_schedule_overrides.length > 0);

    const doctorSchedule = useMemo(() => ({
        default_days: plan.doctor_wear_schedule_default_days ?? plannerSchedule.default_days,
        overrides: plan.doctor_wear_schedule_overrides || [],
    }), [plan.doctor_wear_schedule_default_days, plan.doctor_wear_schedule_overrides, plannerSchedule.default_days]);

    const totalAligners = Math.max(
        Number(plan.aligner_count_upper || 0),
        Number(plan.aligner_count_lower || 0),
    );

    const [dialogOpen, setDialogOpen] = useState(false);
    const [draft, setDraft] = useState(doctorSchedule);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        setDraft(doctorSchedule);
    }, [doctorSchedule.default_days, JSON.stringify(doctorSchedule.overrides)]);

    /* Hide entirely when there's nothing for the planner to have scheduled yet. */
    if (totalAligners === 0 || plannerSchedule.default_days == null) return null;

    const effective = hasDoctorOverride ? doctorSchedule : plannerSchedule;
    const effectiveTotalDays = computeTotalWearDays(totalAligners, effective.default_days, effective.overrides);
    const plannerTotalDays = computeTotalWearDays(totalAligners, plannerSchedule.default_days, plannerSchedule.overrides);

    const saveOverride = async () => {
        setBusy(true);
        try {
            await api.post(`/doctor/cases/${c.id}/wear-schedule-override`, {
                default_days: Number(draft.default_days),
                overrides: (draft.overrides || []).map((o) => ({ stage: Number(o.stage), days: Number(o.days) })),
            });
            toast.success("Wear schedule override saved");
            setDialogOpen(false);
            if (onChange) await onChange();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Couldn't save override");
        } finally {
            setBusy(false);
        }
    };

    const clearOverride = async () => {
        if (!hasDoctorOverride) return;
        setBusy(true);
        try {
            await api.delete(`/doctor/cases/${c.id}/wear-schedule-override`);
            toast.success("Reverted to planner's schedule");
            if (onChange) await onChange();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Couldn't reset override");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="bg-card border border-border rounded-2xl p-5 mb-4" data-testid="doctor-wear-schedule-review">
            <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
                <div className="min-w-0">
                    <div className="overline text-muted-foreground flex items-center gap-1.5">
                        <ClockClockwise size={12} /> Wear schedule
                    </div>
                    <h3 className="font-display text-lg font-bold mt-0.5">
                        {hasDoctorOverride ? "Your override is active" : "Planner's schedule"}
                    </h3>
                    <p className="text-xs text-muted-foreground mt-0.5">
                        {hasDoctorOverride
                            ? `Overridden ${fmtDateTime(plan.doctor_wear_schedule_overridden_at)} — planner's original schedule is preserved for compare.`
                            : "Cadence the planner authored for this case. You can override it before the case is approved."}
                    </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                    {hasDoctorOverride && (
                        <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={clearOverride}
                            disabled={busy || c?.is_locked}
                            data-testid="wear-schedule-clear-override"
                            title={c?.is_locked ? "Case is locked — clinical record frozen" : undefined}
                        >
                            <ArrowsCounterClockwise size={13} weight="bold" className="mr-1" />
                            Reset to planner's
                        </Button>
                    )}
                    <Button
                        type="button"
                        size="sm"
                        onClick={() => setDialogOpen(true)}
                        disabled={c?.is_locked}
                        data-testid="wear-schedule-open-override"
                        title={c?.is_locked ? "Case is locked — clinical record frozen" : undefined}
                    >
                        {hasDoctorOverride ? "Edit override" : "Override schedule"}
                    </Button>
                </div>
            </div>

            <div className="grid sm:grid-cols-4 gap-3">
                <ReadStat label="Default cadence" value={`${effective.default_days} days`} />
                <ReadStat label="Per-aligner overrides" value={effective.overrides?.length || 0} />
                <ReadStat label="Total wear days" value={`${effectiveTotalDays} days`} />
                <ReadStat label="Aligners" value={totalAligners} />
            </div>

            {hasDoctorOverride && plannerTotalDays !== effectiveTotalDays && (
                <div className="mt-3 text-xs px-3 py-2 rounded-lg bg-amber-50 border border-amber-200 text-amber-900">
                    Your override changes total wear days from <strong>{plannerTotalDays}d</strong> (planner) to <strong>{effectiveTotalDays}d</strong> ({effectiveTotalDays > plannerTotalDays ? "+" : ""}{effectiveTotalDays - plannerTotalDays}d). The doctor-facing est. completion on the Treatment tab will reflect your schedule.
                </div>
            )}

            <Dialog open={dialogOpen} onOpenChange={(v) => !busy && setDialogOpen(v)}>
                <DialogContent className="max-w-5xl max-h-[92vh] flex flex-col p-0 gap-0">
                    <DialogHeader className="px-5 py-4 border-b border-border shrink-0">
                        <DialogTitle className="font-display text-lg">Override wear schedule</DialogTitle>
                        <p className="text-xs text-muted-foreground mt-1">
                            Planner's authored schedule on the left (read-only) · your edits on the right. Save to stamp the override on this case.
                        </p>
                    </DialogHeader>
                    <div className="px-5 py-5 overflow-y-auto flex-1 min-h-0">
                        <WearScheduleSection
                            value={draft}
                            onChange={setDraft}
                            totalAligners={totalAligners}
                            fittedAt={c.fitted_at}
                            baselineSchedule={plannerSchedule}
                            baselineLabel="Planner's schedule"
                            testidPrefix="wear-override"
                        />
                    </div>
                    <DialogFooter className="px-5 py-3 border-t border-border bg-secondary/20 shrink-0 flex sm:justify-between gap-2">
                        <div className="text-[11px] text-muted-foreground self-center">
                            Planner: {plannerTotalDays}d · Yours: {computeTotalWearDays(totalAligners, draft.default_days, draft.overrides)}d
                        </div>
                        <div className="flex items-center gap-2">
                            <Button
                                type="button"
                                variant="ghost"
                                onClick={() => { setDraft(plannerSchedule); }}
                                disabled={busy}
                                data-testid="wear-override-reset-to-planner"
                            >
                                Reset to planner's
                            </Button>
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => setDialogOpen(false)}
                                disabled={busy}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="button"
                                onClick={saveOverride}
                                disabled={busy}
                                data-testid="wear-override-save"
                            >
                                <CheckCircle size={14} weight="fill" className="mr-1" />
                                {busy ? "Saving…" : "Save override"}
                            </Button>
                        </div>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}


function ReadStat({ label, value }) {
    return (
        <div className="rounded-lg border border-border bg-secondary/20 px-3 py-2">
            <div className="text-[10px] uppercase font-bold tracking-wide text-muted-foreground">{label}</div>
            <div className="font-display text-base font-bold tabular-nums mt-0.5">{value}</div>
        </div>
    );
}
